import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { LogOut, Users, Calendar, Trash2 } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_OPTS = ["new", "contacted", "confirmed", "declined"];

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [entertainers, setEntertainers] = useState([]);

  const startLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    setUser(null);
  };

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setChecking(false);
    }
  }, []);

  const loadData = useCallback(async () => {
    try {
      const [b, e] = await Promise.all([api.get("/bookings"), api.get("/entertainers")]);
      setBookings(b.data);
      setEntertainers(e.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setChecking(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user?.is_admin) loadData();
  }, [user, loadData]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Status updated");
    } catch {
      toast.error("Failed to update");
    }
  };

  const deleteBooking = async (id) => {
    if (!confirm("Delete this booking?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const deleteEntertainer = async (id) => {
    if (!confirm("Remove this entertainer?")) return;
    try {
      await api.delete(`/entertainers/${id}`);
      setEntertainers((es) => es.filter((e) => e.id !== id));
      toast.success("Removed");
    } catch {
      toast.error("Failed to remove");
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-neutral-950 text-neutral-400 flex items-center justify-center" data-testid="admin-loading">
        <span className="font-serif text-2xl">Loading…</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center px-6" data-testid="admin-login">
        <div className="max-w-md w-full text-center">
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Restricted</p>
          <h1 className="font-serif text-4xl md:text-5xl mt-4">Admin Sign In</h1>
          <p className="mt-4 text-neutral-400">Sign in with the registered Google account to manage bookings and entertainers.</p>
          <Button onClick={startLogin} data-testid="admin-google-login-btn" className="mt-8 bg-[#D4AF37] text-black hover:bg-[#C5A028] rounded-none px-8 py-4 tracking-[0.2em] uppercase text-xs">
            Continue with Google
          </Button>
          <div className="mt-6">
            <a href="/" className="text-xs tracking-[0.2em] uppercase text-neutral-500 hover:text-[#D4AF37]" data-testid="admin-back-home">← Back to site</a>
          </div>
        </div>
      </div>
    );
  }

  if (!user.is_admin) {
    return (
      <div className="min-h-screen bg-neutral-950 text-white flex items-center justify-center px-6">
        <div className="max-w-md text-center">
          <h1 className="font-serif text-4xl">Not authorised</h1>
          <p className="mt-4 text-neutral-400">{user.email} isn't on the admin allowlist.</p>
          <Button onClick={logout} className="mt-8 bg-[#D4AF37] text-black hover:bg-[#C5A028] rounded-none px-8 py-4 tracking-[0.2em] uppercase text-xs" data-testid="admin-logout-btn">
            Sign Out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-950 text-white" data-testid="admin-dashboard">
      <header className="border-b border-neutral-900 sticky top-0 bg-neutral-950/90 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]">Next Bar &amp; Lounge</p>
            <h1 className="font-serif text-2xl">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right hidden sm:block">
              <div className="text-sm">{user.name}</div>
              <div className="text-xs text-neutral-500">{user.email}</div>
            </div>
            <Button onClick={logout} variant="outline" className="border-neutral-800 hover:border-[#D4AF37] text-neutral-300 hover:text-[#D4AF37] rounded-none" data-testid="admin-logout-btn">
              <LogOut size={14} className="mr-2" /> Sign out
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        <Tabs defaultValue="bookings">
          <TabsList className="bg-neutral-900 border border-neutral-800 rounded-none">
            <TabsTrigger value="bookings" data-testid="tab-bookings" className="rounded-none data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Calendar size={14} className="mr-2" /> Bookings ({bookings.length})
            </TabsTrigger>
            <TabsTrigger value="entertainers" data-testid="tab-entertainers" className="rounded-none data-[state=active]:bg-[#D4AF37] data-[state=active]:text-black">
              <Users size={14} className="mr-2" /> Entertainers ({entertainers.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="bookings" className="mt-8">
            {bookings.length === 0 ? (
              <div className="text-neutral-500 py-20 text-center" data-testid="bookings-empty">No bookings yet.</div>
            ) : (
              <div className="space-y-3">
                {bookings.map((b) => (
                  <div key={b.id} className="border border-neutral-800 p-6 grid grid-cols-1 md:grid-cols-12 gap-4 items-start" data-testid={`booking-row-${b.id}`}>
                    <div className="md:col-span-3">
                      <div className="font-serif text-xl">{b.full_name}</div>
                      <div className="text-sm text-neutral-400">{b.email}</div>
                      <div className="text-sm text-neutral-400">{b.phone}</div>
                    </div>
                    <div className="md:col-span-3 text-sm">
                      <div className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mb-1">{b.event_type}</div>
                      <div className="text-neutral-300">{b.attendees} guests</div>
                      <div className="text-neutral-500 text-xs">{b.event_date} · {b.event_time}</div>
                    </div>
                    <div className="md:col-span-3 text-xs text-neutral-400 space-y-1">
                      <div><span className="text-neutral-600">Food:</span> {b.food_package}</div>
                      <div><span className="text-neutral-600">Drinks:</span> {b.drinks_package}</div>
                      {b.special_requests && <div className="text-neutral-500 italic">"{b.special_requests}"</div>}
                    </div>
                    <div className="md:col-span-3 flex items-center gap-2 justify-end">
                      <Select value={b.status} onValueChange={(v) => updateStatus(b.id, v)}>
                        <SelectTrigger className="w-36 bg-neutral-900 border-neutral-800 rounded-none text-xs uppercase tracking-wider" data-testid={`booking-status-${b.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                          {STATUS_OPTS.map((s) => (
                            <SelectItem key={s} value={s} className="focus:bg-neutral-900 focus:text-[#D4AF37]">{s}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <button onClick={() => deleteBooking(b.id)} className="p-2 border border-neutral-800 hover:border-red-500 hover:text-red-500 text-neutral-400" data-testid={`booking-delete-${b.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="entertainers" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {entertainers.map((e) => (
                <div key={e.id} className="border border-neutral-800 p-5" data-testid={`ent-row-${e.id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-serif text-xl">{e.name}</div>
                      <div className="text-xs text-[#D4AF37] tracking-[0.2em] uppercase mt-1">{e.day}</div>
                      <div className="text-sm text-neutral-400 mt-2">{e.genre} · {e.show_time}</div>
                    </div>
                    <button onClick={() => deleteEntertainer(e.id)} className="p-2 border border-neutral-800 hover:border-red-500 hover:text-red-500 text-neutral-400" data-testid={`ent-delete-${e.id}`}>
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
